/**
 * usePublicPage — fetches a published Page Builder page by slug.
 *
 * Public pages are returned without authentication.
 * Private pages require a valid session cookie — the hook surfaces
 * a `requiresAuth` flag on error so the caller can redirect to sign-in.
 *
 * `enabled` lets a caller that has already resolved the page from a committed
 * local template skip the request entirely. It defaults to true, so every
 * existing call site behaves exactly as before.
 */

import { useQuery } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export function usePublicPage(pageSlug, { enabled = true } = {}) {
  const path = `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/pages/${pageSlug}/`;

  return useQuery({
    queryKey: ['public-page', pageSlug],
    queryFn: async () => {
      // credentials: 'include' sends the session cookie so the backend can
      // authenticate the user for private pages.
      const resp = await apiHelper.publicApiCall(path, {
        method: 'GET',
        credentials: 'include',
      });

      if (resp.status === 401) {
        const body = await resp.json().catch(() => ({}));
        if (body.requires_auth) {
          const err = new Error('Authentication required');
          err.requiresAuth = true;
          err.status = 401;
          throw err;
        }
        const err = new Error('Unauthorised');
        err.status = 401;
        throw err;
      }

      if (resp.status === 404) {
        const err = new Error('Page not found');
        err.status = 404;
        throw err;
      }

      if (!resp.ok) {
        const err = new Error('Failed to load page');
        err.status = resp.status;
        throw err;
      }

      return resp.json();
    },
    enabled: !!pageSlug && enabled,
    retry: false,
    staleTime: 0,
  });
}
